import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  TextField,
  IconButton,
  MenuItem,
  InputAdornment,
  Button,
  CircularProgress,
  Modal,
  Box,
  Backdrop,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  List
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import EditIcon from '@material-ui/icons/Edit';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  updateSmsProviderAction,
  enableSmsNotifications,
  disableSmsNotifications
} from '../redux/actions/client-actions';
import { SnackbarComponent } from '../../../../core/utils/components/snackbar-component';
import { makeStyles } from '@material-ui/core/styles';
import { getSMSApiKeys } from '../../data/datasources/get-sms-api-keys';
import { SettingRowText } from './setting-row-text';

const useStyles = makeStyles((theme) => ({
  modal: {
    width: 680,
    backgroundColor: '#fff',
    position: 'absolute',
    borderRadius: 5,
    top: '25%',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: theme.spacing(4)
  },
  formButtons: {
    marginTop: theme.spacing(2)
  },
  submitButton: {
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark
    }
  },
  cancelButton: {
    marginTop: theme.spacing(2),
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main
  },
  sms_edit: {
    marginRight: theme.spacing(1)
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff'
  },
  root: {
    marginTop: theme.spacing(2),
    width: '100%',
    backgroundColor: theme.palette.background.paper
  },
  granted: {
    color: '#4caf50'
  }
}));

// This row is shown for whichever provider the client is on, and what the pencil asks for differs
// by provider: cm.com takes a single product token and has no shared Loop account behind it, while
// SMS Portal takes an id + secret pair and does still fall back to Loop's shared account. A single
// cm.com-worded hint would tell an SMS Portal operator to paste the wrong credential entirely.
const SMS_CREDENTIAL_HINT = {
  cmcom:
    'The pencil takes this client’s own cm.com product token — there is no shared Loop token for customer SMS.',
  smsportal: 'The pencil takes this client’s own SMS Portal Client ID and API Secret.'
};

const CUSTOMER_SMS_HINT = (provider) =>
  `Sends order updates to this client’s customers by SMS. ${
    SMS_CREDENTIAL_HINT[provider] || 'The pencil takes this client’s own provider credentials.'
  }`;

export function CustomerSmsNotificationsComponent({ client }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [showApiKey, setShowApiKey] = useState([]);
  const [open, setOpen] = useState(false);
  const [backdrop, setBackdrop] = useState(false);
  const [snackbar, setSnackbar] = useState(false);
  const [message, setMessage] = useState({ status: 'success', description: 'Permission updated' });
  const [apiKeys, setApiKeys] = useState(['']);

  const closeSnackbar = () => setSnackbar(false);
  const startLoader = () => setBackdrop(true);
  const closeLoader = () => setBackdrop(false);

  const showSnackbar = (status = 'success', description = 'Permission updated') => {
    setSnackbar(true);
    setMessage({ status, description });
    closeLoader();
  };

  const enableSms = () => {
    startLoader();
    const props = { closeLoader, showSnackbar };
    dispatch(enableSmsNotifications(client.id, props));
  };

  const disableSms = () => {
    startLoader();
    const props = { closeLoader, showSnackbar };
    dispatch(disableSmsNotifications(client.id, props));
  };

  const handleOpen = async () => {
    try {
      startLoader();
      const { keys } = await getSMSApiKeys(client.id);
      setApiKeys(keys.length ? keys : ['']);
      setShowApiKey(new Array(keys.length || 1).fill(false));
      setOpen(true);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    } finally {
      closeLoader();
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleProviderChange = (e, setFieldValue) => {
    const selectedProvider = e.target.value;
    let newApiKeys = [];
    if (selectedProvider === 'smsportal') {
      newApiKeys = ['', ''];
    } else if (selectedProvider === 'cmcom') {
      newApiKeys = [''];
    }
    setFieldValue('smsProvider', selectedProvider);
    setFieldValue('apiKeys', newApiKeys);
    setShowApiKey(new Array(newApiKeys.length).fill(false));
  };

  const handleToggleShowApiKey = (index) => {
    setShowApiKey((prevState) => {
      const newState = [...prevState];
      newState[index] = !newState[index];
      return newState;
    });
  };

  return (
    <>
      <List className={classes.root}>
        <ListItem>
          <ListItemIcon>
            {client.settings.notifications?.sms?.enabled ? (
              <FiberManualRecordIcon className={classes.granted} />
            ) : (
              <FiberManualRecordIcon color="error" />
            )}
          </ListItemIcon>
          <SettingRowText
            title="Customer SMS Notifications"
            hint={CUSTOMER_SMS_HINT(client.settings.notifications?.sms?.provider)}
          />
          <ListItemSecondaryAction>
            {client.settings.notifications?.sms?.enabled && (
              <IconButton className={classes.sms_edit} edge="end" onClick={handleOpen}>
                <EditIcon />
              </IconButton>
            )}
            {client.settings.notifications?.sms?.enabled ? (
              <Button
                type="button"
                variant="outlined"
                size="small"
                color="secondary"
                onClick={disableSms}
              >
                Disable
              </Button>
            ) : (
              <Button
                type="button"
                variant="contained"
                size="small"
                color="secondary"
                onClick={enableSms}
              >
                Enable
              </Button>
            )}
          </ListItemSecondaryAction>
        </ListItem>
      </List>

      <Modal open={open} onClose={handleClose} aria-labelledby="sms-modal-title">
        <Box className={classes.modal}>
          <h2 id="sms-modal-title">Customer SMS Provider Settings</h2>
          <Formik
            enableReinitialize
            initialValues={{
              smsProvider: client.settings.notifications?.sms?.provider || '',
              apiKeys: apiKeys.length ? apiKeys : ['']
            }}
            validationSchema={Yup.object().shape({
              smsProvider: Yup.string().required('SMS Provider is required'),
              apiKeys: Yup.array().test('apiKeys', 'Required', function (value) {
                const { smsProvider } = this.parent;
                if (smsProvider === 'cmcom') {
                  return value[0] && value[0].trim() !== '';
                } else if (smsProvider === 'smsportal') {
                  return value.every((key) => key && key.trim() !== '');
                }
                return false;
              })
            })}
            onSubmit={(values) => {
              // Compare against what the dialog was opened with. The old comparison read
              // settings.notifications.sms.apiKeys, a property the Client entity never populates
              // (it writes api_key_1 / api_key_2), so every Submit stored a redundant secret
              // version.
              const hasApiKeyChanged = values.apiKeys.some(
                (key, index) => (key?.trim() || '') !== (apiKeys[index]?.trim() || '')
              );

              if (
                values.smsProvider !== client.settings.notifications.sms.provider ||
                hasApiKeyChanged
              ) {
                dispatch(
                  updateSmsProviderAction(
                    client.id,
                    values.smsProvider,
                    values.apiKeys[0] || '',
                    values.apiKeys[1] || '',
                    { startLoader, closeLoader, showSnackbar }
                  )
                );
              }
              handleClose();
            }}
          >
            {({
              values,
              setFieldValue,
              handleChange,
              handleBlur,
              handleSubmit,
              errors,
              touched
            }) => (
              <form noValidate autoComplete="off" onSubmit={handleSubmit}>
                <TextField
                  select
                  name="smsProvider"
                  value={values.smsProvider}
                  onChange={(e) => handleProviderChange(e, setFieldValue)}
                  onBlur={handleBlur}
                  error={touched.smsProvider && Boolean(errors.smsProvider)}
                  helperText={touched.smsProvider ? errors.smsProvider : ''}
                  label="SMS Provider"
                  variant="outlined"
                  fullWidth
                >
                  <MenuItem value="smsportal">SMS Portal</MenuItem>
                  <MenuItem value="cmcom">CM.com</MenuItem>
                </TextField>

                {values.smsProvider === 'smsportal' && (
                  <>
                    <TextField
                      name="apiKeys[0]"
                      value={values.apiKeys[0] || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Client ID"
                      type={showApiKey[0] ? 'text' : 'password'}
                      variant="outlined"
                      fullWidth
                      required
                      margin="normal"
                      error={touched.apiKeys && Boolean(errors.apiKeys)}
                      helperText={touched.apiKeys && errors.apiKeys ? errors.apiKeys : ''}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => handleToggleShowApiKey(0)}>
                              {showApiKey[0] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                    <TextField
                      name="apiKeys[1]"
                      value={values.apiKeys[1] || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="API Secret"
                      type={showApiKey[1] ? 'text' : 'password'}
                      variant="outlined"
                      fullWidth
                      required
                      margin="normal"
                      error={touched.apiKeys && Boolean(errors.apiKeys)}
                      helperText={touched.apiKeys && errors.apiKeys ? errors.apiKeys : ''}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => handleToggleShowApiKey(1)}>
                              {showApiKey[1] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </>
                )}

                {values.smsProvider === 'cmcom' && (
                  <TextField
                    name="apiKeys[0]"
                    value={values.apiKeys[0] || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Product Token"
                    type={showApiKey[0] ? 'text' : 'password'}
                    variant="outlined"
                    fullWidth
                    required
                    margin="normal"
                    error={touched.apiKeys && Boolean(errors.apiKeys)}
                    helperText={
                      touched.apiKeys && errors.apiKeys
                        ? errors.apiKeys
                        : 'This client’s own cm.com product token'
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => handleToggleShowApiKey(0)}>
                            {showApiKey[0] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                )}

                <div className={classes.formButtons}>
                  <Button
                    className={classes.submitButton}
                    type="submit"
                    variant="contained"
                    size="large"
                    color="primary"
                    fullWidth
                  >
                    Submit
                  </Button>
                </div>
              </form>
            )}
          </Formik>

          <Button
            className={classes.cancelButton}
            variant="outlined"
            size="large"
            color="secondary"
            fullWidth
            onClick={handleClose}
          >
            Cancel
          </Button>
        </Box>
      </Modal>

      <Backdrop className={classes.backdrop} open={backdrop} onClick={closeLoader}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <SnackbarComponent
        open={snackbar}
        onClose={closeSnackbar}
        severity={message.status}
        message={message.description}
      />
    </>
  );
}
